import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://hambre.app';

// SEO configurations for all pages
// Updated Feb 2026 - Hambre is now a complete Restaurant Operating System
export const seoConfig = {
  home: {
    title: 'Hambre - Restaurant Operating System | POS, KDS, Delivery & Table Management',
    description: 'Complete restaurant management platform with Point of Sale, Kitchen Display System, Table Management, Online Ordering, Delivery Tracking, Staff Management, and VeriFactu compliance. All-in-one solution for modern restaurants.',
    keywords: 'restaurant management system, restaurant POS, kitchen display system, KDS, table management, online ordering, delivery management, restaurant software, restaurant operating system',
    image: '/images/og-restaurant-os.png',
    path: '/',
    noindex: false,
  },
  restaurantOS: {
    title: 'Restaurant OS - All-in-One Restaurant Management Platform | Hambre',
    description: 'Transform your restaurant with Hambre\'s complete operating system. POS, Kitchen Display, Table QR Ordering, Delivery Management, Staff Scheduling, Analytics, and VeriFactu tax compliance - all in one platform.',
    keywords: 'restaurant operating system, restaurant management software, POS system, kitchen display, QR ordering, delivery management, restaurant analytics, VeriFactu Spain, restaurant technology',
    image: '/images/og-restaurant-os.png',
    path: '/restaurant-os',
    noindex: false,
  },
  restaurants: {
    title: 'Browse Restaurants | Order Online | Hambre',
    description: 'Discover and order from hundreds of local restaurants. Filter by cuisine, delivery, takeaway, or dine-in. Fast delivery and easy online ordering.',
    keywords: 'restaurants near me, order food online, local restaurants, cuisine, takeaway, dine-in, food delivery',
    image: '/images/og-restaurants.png',
    path: '/restaurants',
    noindex: false,
  },
  verifactu: {
    title: 'VeriFactu - Spain Tax Compliance for Restaurants | Hambre',
    description: 'Automatic VeriFactu compliance for Spanish restaurants. Tamper-proof invoices, AEAT submission, QR verification codes, and complete audit trail. Meet Spain\'s anti-fraud requirements effortlessly.',
    keywords: 'VeriFactu, Spain tax compliance, AEAT, restaurant invoicing, anti-fraud system, Spanish restaurants, fiscal compliance, electronic invoicing Spain',
    image: '/images/og-verifactu.png',
    path: '/verifactu',
    noindex: false,
  },
  pos: {
    title: 'Restaurant POS System | Touch-Optimized Till | Hambre',
    description: 'Modern restaurant POS system designed for speed. Touch-optimized interface, bill splitting, multiple payment methods, receipt printing, and offline mode. Works on any device.',
    keywords: 'restaurant POS, point of sale, till system, restaurant checkout, payment processing, bill splitting, restaurant payments',
    image: '/images/og-pos.png',
    path: '/features/pos',
    noindex: false,
  },
  kitchenDisplay: {
    title: 'Kitchen Display System (KDS) | Real-time Order Management | Hambre',
    description: 'Streamline your kitchen workflow with real-time order display. Station routing, prep timers, allergy alerts, and bump-to-complete functionality. Reduce errors and speed up service.',
    keywords: 'kitchen display system, KDS, restaurant kitchen management, order display, prep timers, kitchen workflow, restaurant efficiency',
    image: '/images/og-kds.png',
    path: '/features/kitchen-display',
    noindex: false,
  },
  tableManagement: {
    title: 'Table Management & QR Ordering | Hambre Restaurant OS',
    description: 'Digital table management with QR code ordering. Guests scan, browse menu, and order from their phones. Real-time updates to kitchen, waiter alerts, and seamless bill management.',
    keywords: 'table management, QR ordering, contactless ordering, digital menu, restaurant tables, table booking, guest ordering',
    image: '/images/og-tables.png',
    path: '/features/table-management',
    noindex: false,
  },
  deliveryManagement: {
    title: 'Delivery Management System | Driver Tracking | Hambre',
    description: 'Complete delivery management with real-time driver tracking, route optimization, customer notifications, and delivery zones. Manage your own delivery fleet or use our network.',
    keywords: 'delivery management, driver tracking, delivery optimization, restaurant delivery, online ordering, delivery zones, fleet management',
    image: '/images/og-delivery.png',
    path: '/features/delivery',
    noindex: false,
  },
  staffManagement: {
    title: 'Restaurant Staff Management | Scheduling & Roles | Hambre',
    description: 'Manage your restaurant team with role-based access, shift scheduling, performance tracking, and waiter management. PIN-based login and detailed activity logs.',
    keywords: 'staff management, restaurant scheduling, employee management, waiter management, shift planning, restaurant HR, staff roles',
    image: '/images/og-staff.png',
    path: '/features/staff',
    noindex: false,
  },
  analytics: {
    title: 'Restaurant Analytics & Reporting | Hambre',
    description: 'Make data-driven decisions with comprehensive restaurant analytics. Sales reports, popular items, peak hours, staff performance, and end-of-day summaries.',
    keywords: 'restaurant analytics, sales reporting, restaurant data, business intelligence, EOD reports, performance tracking, restaurant insights',
    image: '/images/og-analytics.png',
    path: '/features/analytics',
    noindex: false,
  },
  stockManagement: {
    title: 'Restaurant Inventory & Stock Management | Hambre',
    description: 'Track ingredients, manage suppliers, set low stock alerts, and reduce waste. AI-powered receipt scanning for quick inventory updates.',
    keywords: 'inventory management, stock control, restaurant supplies, ingredient tracking, supplier management, food waste reduction, AI receipt scanning',
    image: '/images/og-stock.png',
    path: '/features/stock',
    noindex: false,
  },
  loyaltyProgram: {
    title: 'Customer Loyalty Program | Hambre Restaurant OS',
    description: 'Build customer loyalty with points, rewards, and special offers. Increase repeat visits and customer lifetime value with automated loyalty tracking.',
    keywords: 'loyalty program, customer rewards, restaurant loyalty, points system, customer retention, repeat customers, restaurant marketing',
    image: '/images/og-loyalty.png',
    path: '/features/loyalty',
    noindex: false,
  },
  about: {
    title: 'About Hambre | Restaurant Technology Company',
    description: 'Hambre is transforming restaurant management with modern technology. From food delivery to complete restaurant operations, we help restaurants thrive in the digital age.',
    keywords: 'about hambre, restaurant technology, restaurant software company, food tech, restaurant innovation',
    image: '/images/og-about.png',
    path: '/about',
    noindex: false,
  },
  contact: {
    title: 'Contact Hambre | Restaurant Support & Sales',
    description: 'Get in touch with Hambre for restaurant software demos, customer support, or partnership inquiries. We\'re here to help your restaurant succeed.',
    keywords: 'contact hambre, restaurant support, software demo, restaurant sales, customer service',
    image: '/images/og-contact.png',
    path: '/contact',
    noindex: false,
  },
  faq: {
    title: 'FAQ | Restaurant OS & Food Delivery Help | Hambre',
    description: 'Find answers about restaurant management features, food delivery, payments, and more. Get help with Hambre\'s restaurant operating system.',
    keywords: 'faq, help, restaurant software help, POS help, delivery questions, support',
    image: '/images/og-faq.png',
    path: '/faq',
    noindex: false,
  },
  cart: {
    title: 'Your Cart | Hambre',
    description: 'Review your order and checkout securely. Fast delivery from your favorite local restaurants.',
    keywords: 'shopping cart, checkout, food order, secure payment',
    image: '/images/og-home.png',
    path: '/cart',
    noindex: false,
  },
  orders: {
    title: 'Your Orders | Hambre',
    description: 'View your order history and track current deliveries. Reorder your favorite meals with one click.',
    keywords: 'order history, track delivery, my orders, food delivery tracking',
    image: '/images/og-orders.png',
    path: '/orders',
    noindex: false,
  },
  driverApplication: {
    title: 'Become a Delivery Driver | Flexible Hours | Hambre',
    description: 'Join Hambre as a delivery driver. Flexible hours, competitive pay, real-time order tracking, and be your own boss. Apply now and start earning.',
    keywords: 'delivery driver jobs, food delivery driver, earn money, flexible work, driver application, gig economy',
    image: '/images/og-driver.png',
    path: '/apply/driver',
    noindex: false,
  },
  salespersonApplication: {
    title: 'Become a Sales Partner | Restaurant Partnerships | Hambre',
    description: 'Help restaurants modernize with Hambre\'s operating system and earn lifetime commission. Join our sales partner program today.',
    keywords: 'sales partner, restaurant partnerships, earn commission, sales jobs, restaurant software sales',
    image: '/images/og-salesperson.png',
    path: '/apply/salesperson',
    noindex: false,
  },
  terms: {
    title: 'Terms of Service | Hambre Restaurant OS',
    description: 'Terms of service and conditions for using Hambre\'s restaurant operating system and food delivery platform.',
    keywords: 'terms of service, terms and conditions, legal, user agreement',
    image: '/images/og-home.png',
    path: '/terms',
    noindex: false,
  },
  privacy: {
    title: 'Privacy Policy | Hambre',
    description: 'How Hambre protects your privacy and handles data for restaurants and customers. GDPR compliant.',
    keywords: 'privacy policy, data protection, GDPR, personal data, privacy',
    image: '/images/og-home.png',
    path: '/privacy',
    noindex: false,
  },
  contractorTerms: {
    title: 'Contractor Terms | Drivers & Partners | Hambre',
    description: 'Terms and conditions for Hambre contractors, drivers, and sales partners.',
    keywords: 'contractor terms, driver terms, partner agreement',
    image: '/images/og-home.png',
    path: '/contractor-terms',
    noindex: false,
  },
  profile: {
    title: 'My Profile | Hambre',
    description: 'Manage your Hambre account settings, addresses, and preferences.',
    keywords: 'profile, account settings, my account',
    image: '/images/og-home.png',
    path: '/profile',
    noindex: true,
  },
  // Auth pages - noindex
  login: {
    title: 'Login | Hambre Restaurant OS',
    description: 'Sign in to your Hambre account to manage your restaurant or order food.',
    keywords: 'login, sign in',
    image: '/images/og-home.png',
    path: '/login',
    noindex: true,
  },
  register: {
    title: 'Sign Up | Hambre Restaurant OS',
    description: 'Create your Hambre account to manage your restaurant or start ordering.',
    keywords: 'sign up, register, create account',
    image: '/images/og-home.png',
    path: '/register',
    noindex: true,
  },
  forgotPassword: {
    title: 'Forgot Password | Hambre',
    description: 'Reset your Hambre account password.',
    keywords: 'forgot password, reset password',
    image: '/images/og-home.png',
    path: '/forgot-password',
    noindex: true,
  },
  // Restaurant Admin pages - noindex
  restaurantDashboard: {
    title: 'Restaurant Dashboard | Hambre',
    description: 'Manage your restaurant with Hambre\'s comprehensive dashboard.',
    keywords: 'restaurant dashboard, management',
    image: '/images/og-home.png',
    path: '/restaurant',
    noindex: true,
  },
};

export default function SEO({ page, customTitle, customDescription, customImage, customKeywords }) {
  const config = seoConfig[page] || seoConfig.home;
  
  const title = customTitle || config.title;
  const description = customDescription || config.description;
  const image = customImage || config.image;
  const keywords = customKeywords || config.keywords;
  const url = `${BASE_URL}${config.path}`;
  const imageUrl = image.startsWith('http') ? image : `${BASE_URL}${image}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Robots */}
      {config.noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Hambre Restaurant OS" />
      <meta property="og:locale" content="en_GB" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
}

// Restaurant detail page SEO - dynamic
export function RestaurantSEO({ restaurant }) {
  if (!restaurant) return null;
  
  const title = `${restaurant.name} | Order Online | Hambre`;
  const description = restaurant.description || `Order from ${restaurant.name}. ${restaurant.cuisine_type?.join(', ')}. Fast delivery, takeaway, or dine-in. Easy online ordering with Hambre.`;
  const url = `${BASE_URL}/restaurant/${restaurant.id}`;
  const image = restaurant.image_url || `${BASE_URL}/images/og-restaurants.png`;
  
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={`${restaurant.name}, ${restaurant.cuisine_type?.join(', ')}, order online, food delivery, ${restaurant.city}, restaurant`} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />
      
      <meta property="og:type" content="restaurant" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Hambre Restaurant OS" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Schema.org structured data for restaurants */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Restaurant",
          "name": restaurant.name,
          "description": restaurant.description,
          "image": image,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": restaurant.address,
            "addressLocality": restaurant.city,
            "postalCode": restaurant.postal_code,
          },
          "telephone": restaurant.phone,
          "servesCuisine": restaurant.cuisine_type,
          "aggregateRating": restaurant.rating ? {
            "@type": "AggregateRating",
            "ratingValue": restaurant.rating,
            "bestRating": "5",
          } : undefined,
        })}
      </script>
    </Helmet>
  );
}

// Software Application SEO - for Restaurant OS pages
export function SoftwareSEO({ feature }) {
  const title = `${feature?.name || 'Restaurant OS'} | Hambre`;
  const description = feature?.description || 'Complete restaurant management platform.';
  const url = `${BASE_URL}${feature?.path || '/restaurant-os'}`;
  const image = feature?.image || `${BASE_URL}/images/og-restaurant-os.png`;
  
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Hambre Restaurant OS" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Schema.org structured data for software */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Hambre Restaurant OS",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web, iOS, Android",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "GBP",
            "description": "Free to start, pay as you grow"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "250"
          },
          "featureList": [
            "Point of Sale (POS)",
            "Kitchen Display System",
            "Table Management",
            "Online Ordering",
            "Delivery Management",
            "Staff Management",
            "Analytics & Reporting",
            "VeriFactu Compliance"
          ]
        })}
      </script>
    </Helmet>
  );
}
